import { Chart } from 'chart.js';

interface ImageCacheEntry {
  img: HTMLImageElement;
  loaded: boolean;
  error: boolean;
}

const iconCache = new Map<Chart, ImageCacheEntry[]>();

const loadImage = (src: string, chart: Chart): HTMLImageElement => {
  const img = new Image();
  img.src = src;

  img.onload = () => {
    const cache = iconCache.get(chart);
    if (cache) {
      const entry = cache.find(entry => entry.img === img);
      if (entry && !entry.loaded) {
        entry.loaded = true;
        // Trigger a re-render once the image is loaded
        chart.update('none');
      }
    }
  };

  img.onerror = () => {
    const cache = iconCache.get(chart);
    if (cache) {
      const entry = cache.find(entry => entry.img === img);
      if (entry) {
        entry.loaded = true;
        // Trigger a re-render once the image is loaded
        chart.update('none');
      }
    }
  };

  // img.crossOrigin = "anonymous";

  return img;
};

export const customPlugin = {
  id: 'customPlugin',

  beforeUpdate: (chart: Chart) => {
    const opts = (chart as any).options?.customPlugin;
    if (!opts) return;

    const images: string[] = (chart.data?.datasets as any)?.[0]?.images;
    if (!images?.length) {
      iconCache.set(chart, []);
      return;
    }

    const cache = iconCache.get(chart) ?? [];
    const needsUpdate = cache.length !== images.length || cache.some((entry, i) => entry.img?.src !== images[i]);

    if (needsUpdate) {
      iconCache.set(
        chart,
        images.map(src => {
          const img = loadImage(src, chart);
          return { img, loaded: img.complete && img.naturalHeight !== 0, error: img.complete && img.naturalHeight === 0 };
        })
      );
    }
  },

  afterDatasetsDraw: (chart: Chart) => {
    if (!(chart as any).options?.customPlugin) return;

    const cache = iconCache.get(chart);
    const xAxis = (chart.scales as any)['x'];
    if (!cache?.length || !xAxis) return;

    const ctx = chart.ctx;
    const width = 24;

    cache.forEach((entry, index) => {
      if (entry.loaded && !entry.error) {
        const x = xAxis.getPixelForValue(index);
        try {
          ctx.drawImage(entry.img, x - 10, xAxis.top + 5, width, width);
        } catch (e) {
          console.error(`Failed to draw image at index ${index}:`, e);
        }
      } else if (entry.error) {
        console.warn(`Skipping broken image at index ${index}: ${entry.img.src}`);
      }
    });
  },
};
